import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Code2, Database, Bot, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
};

const productTypes = [
  {
    icon: TrendingUp,
    title: "Revenue-First SaaS",
    description: "Software designed to generate revenue from day one. We focus on what customers will pay for, not what looks impressive.",
    includes: [
      "Customer-validated features",
      "Pricing-validated from start",
      "Built to iterate quickly",
      "Scalable architecture",
    ],
  },
  {
    icon: Code2,
    title: "Lean MVPs",
    description: "The smallest thing you can build to learn the most. No gold-plating. No over-engineering. Just what you need to validate.",
    includes: [
      "Problem-focused scope",
      "Fast to market",
      "Easy to change",
      "Learning-optimized",
    ],
  },
  {
    icon: Bot,
    title: "AI-Enabled Internal Systems",
    description: "Tools that remove operational burden. AI where it actually helps, not where it is just a checkbox.",
    includes: [
      "Workflow automation",
      "Decision support",
      "Data processing",
      "Genuine efficiency gains",
    ],
  },
  {
    icon: Database,
    title: "Products Built to Grow",
    description: "We do not just build to launch. We build with the future in mind, without over-engineering for problems you do not have yet.",
    includes: [
      "Clean, maintainable code",
      "Sensible architecture",
      "Documentation",
      "Handoff-ready",
    ],
  },
];

const principles = [
  { do: "Build what customers will pay for", dont: "Build features that sound cool" },
  { do: "Start small and iterate", dont: "Launch with everything at once" },
  { do: "Use proven technology", dont: "Chase bleeding-edge for its own sake" },
  { do: "Document and prepare for handoff", dont: "Create founder-only tribal knowledge" },
];

// Hand holding book with sketches illustration
const illustration = "/images/what-we-build.png";

export default function WhatWeBuildPage() {
  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="bg-hero py-16 sm:py-20 lg:py-24">
        <div className="container-narrow">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeUp}
            className="text-center"
          >
            <h1 className="heading-display text-foreground mb-6">
              What We Build
            </h1>
            <p className="body-large text-muted-foreground max-w-2xl mx-auto">
              Products designed for revenue and growth, not just launches.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro with Illustration */}
      <section className="bg-background py-12 sm:py-16">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Illustration */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7 }}
              className="flex justify-center order-2 lg:order-1"
            >
              <div className="w-full max-w-xs">
                <img
                  src={illustration}
                  alt="From ideas to structured products"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="order-1 lg:order-2"
            >
              <h2 className="heading-section text-foreground mb-5">Built Right, Not Just Built Fast</h2>
              <p className="body-base text-muted-foreground mb-4">
                We specialize in products that generate revenue or remove operational burden. Every product we build is designed with growth in mind, without over-engineering.
              </p>
              <p className="body-base text-muted-foreground">
                We focus on what matters: customer validation, clean architecture, and products that can scale when the time is right.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Types */}
      <section className="bg-section py-16 sm:py-20 lg:py-24">
        <div className="container-base">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="heading-large text-foreground mb-5">Types of Products</h2>
            <p className="body-base text-muted-foreground">
              We specialize in products that generate revenue or remove operational burden.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {productTypes.map((type, i) => (
              <motion.div
                key={type.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <Card className="card-elevated h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="icon-container">
                        <type.icon className="h-5 w-5 text-accent" />
                      </div>
                      <h3 className="font-serif text-xl font-medium text-foreground">{type.title}</h3>
                    </div>
                    <p className="text-muted-foreground mb-4">{type.description}</p>
                    <ul className="space-y-2">
                      {type.includes.map((item, j) => (
                        <li key={j} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0" />
                          <span className="text-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="bg-background py-16 sm:py-20">
        <div className="container-base">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="heading-section text-foreground mb-5">Our Building Principles</h2>
            <p className="body-base text-muted-foreground">
              What we do, and what we deliberately avoid.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {principles.map((principle, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-card border border-border/50 rounded-xl p-5"
              >
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                    <span className="text-foreground font-medium">{principle.do}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5 text-center">×</span>
                    <span className="text-muted-foreground">{principle.dont}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-primary-foreground py-16 sm:py-20">
        <div className="container-narrow text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="heading-section mb-5">Have Something to Build?</h2>
            <p className="body-base text-primary-foreground/80 mb-6">
              Tell us what you are thinking. We will tell you honestly if we can help.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button variant="secondary" size="lg" asChild className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 rounded-full">
                <Link to="/contact">
                  Submit Your Wishlist
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 rounded-full">
                <Link to="/process">See Our Process</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
